import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Wallet, Plus, Trash, ArrowClockwise, Users, MapTrifold, Compass } from "@phosphor-icons/react";

const DEFAULT_ZONES = [
  { code: "1A", upper_km: 5 },
  { code: "1B", upper_km: 10 },
  { code: "2", upper_km: 20 },
  { code: "3", upper_km: 30 },
  { code: "4", upper_km: 40 },
  { code: "5", upper_km: null },
];

// Attach a stable UI-only identifier to each zone so React's reconciler can
// track them across add / remove / edit (code + upper_km are user-editable so
// they can't be used as keys).
let _zoneUidSeq = 0;
const withUid = (z) => ({ ...z, _uid: z._uid ?? `z-${++_zoneUidSeq}-${Math.random().toString(36).slice(2, 7)}` });

const SOURCES = [
  { key: "address", label: "Adresse chantier uniquement", hint: "Fiable — même hors ligne. Recommandé tant que le GPS SaaS n'est pas au point.", icon: MapTrifold },
  { key: "mix", label: "Mix (adresse + GPS en secours)", hint: "Utilise l'adresse si dispo, sinon prend le point GPS le plus éloigné du jour.", icon: Compass },
  { key: "gps", label: "GPS uniquement", hint: "Basé sur les traces GPS du tech (nécessite le consentement RGPD activé).", icon: Compass },
];

const ROLE_LABEL = { admin: "Gérant", compta: "Compta", associate: "Associé", technician: "Tech", external: "Externe", manager: "Manager" };

/**
 * Compta-editable panneau : zones URSSAF + source de distance + techs inclus.
 * Persisted server-side under `app_settings.id = "payroll"`.
 */
export default function PayrollZonesCard() {
  const nav = useNavigate();
  const [zones, setZones] = useState(null);
  const [dataSource, setDataSource] = useState("address");
  const [excluded, setExcluded] = useState([]);
  const [team, setTeam] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/payroll/settings"),
      api.get("/users"),
    ]).then(([s, u]) => {
      setZones((s.data?.zones || DEFAULT_ZONES).map(withUid));
      setDataSource(s.data?.data_source || "address");
      setExcluded(s.data?.excluded_user_ids || []);
      setTeam((u.data || []).filter((x) => x.is_active !== false));
    }).catch(() => {
      setZones(DEFAULT_ZONES.map(withUid));
    });
  }, []);

  const patchZone = (idx, updates) => setZones((zs) => zs.map((z, i) => (i === idx ? { ...z, ...updates } : z)));
  const addZone = () => {
    setZones((zs) => {
      const openEndIdx = zs.findIndex((z) => z.upper_km === null || z.upper_km === undefined);
      const previousUpper = openEndIdx > 0 ? (zs[openEndIdx - 1]?.upper_km ?? 0) : (zs[zs.length - 1]?.upper_km ?? 0);
      const draft = withUid({ code: "", upper_km: Number(previousUpper) + 10 });
      if (openEndIdx === -1) return [...zs, draft];
      const copy = [...zs];
      copy.splice(openEndIdx, 0, draft);
      return copy;
    });
  };
  const removeZone = (idx) => setZones((zs) => zs.filter((_, i) => i !== idx));
  const resetDefaults = () => setZones(DEFAULT_ZONES.map(withUid));

  const toggleUser = (uid) => {
    setExcluded((cur) => cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid]);
  };

  const includedCount = team.filter((u) => !excluded.includes(u.id)).length;

  const save = async () => {
    if (!zones || zones.length === 0) {
      toast.error("Au moins une zone est requise");
      return;
    }
    const codes = new Set();
    for (const z of zones) {
      if (!z.code || !String(z.code).trim()) { toast.error("Chaque zone doit avoir un code"); return; }
      if (codes.has(z.code)) { toast.error(`Code en doublon : ${z.code}`); return; }
      codes.add(z.code);
    }
    setSaving(true);
    try {
      await api.patch("/payroll/settings", {
        zones: zones.map((z) => ({
          code: String(z.code).trim(),
          upper_km: z.upper_km === null || z.upper_km === "" || z.upper_km === undefined ? null : Number(z.upper_km),
        })),
        data_source: dataSource,
        excluded_user_ids: excluded,
      });
      toast.success("Paramètres enregistrés");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (zones === null) {
    return <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 text-sm text-white/50">Chargement…</div>;
  }

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-4" data-testid="payroll-zones-card">
      {/* Header */}
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl bg-[#22C55E]/15 border border-[#22C55E]/30 flex items-center justify-center">
          <Wallet size={17} weight="duotone" className="text-[#4ADE80]" />
        </div>
        <div className="flex-1">
          <div className="font-semibold text-sm">Paniers &amp; Indemnités de trajet</div>
          <div className="text-[11px] text-white/50">Zones URSSAF, source de distance et personnes concernées.</div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); nav("/heures"); }}
          className="text-[11px] px-2.5 py-1 rounded-full bg-[#005CFF]/15 border border-[#005CFF]/35 text-[#7CB2FF] hover:bg-[#005CFF]/25 active:scale-[0.98] transition"
          data-testid="zones-open-heures"
          title="Voir Heures & km"
        >
          Voir /heures →
        </button>
      </div>

      {/* Zones grid */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Zones (km)</div>
        <div className="rounded-lg border border-white/10 overflow-hidden">
          <div className="grid grid-cols-[80px_1fr_60px] gap-2 px-3 py-2 bg-white/5 text-[10px] uppercase tracking-widest text-white/50">
            <div>Code</div>
            <div>Distance (à vol d&apos;oiseau)</div>
            <div className="text-right"></div>
          </div>
          {zones.map((z, i) => {
            const prev = i > 0 ? zones[i - 1] : null;
            const lowerLabel = prev && prev.upper_km !== null ? `${prev.upper_km} km` : "0 km";
            const isOpenEnd = z.upper_km === null || z.upper_km === undefined || z.upper_km === "";
            return (
              <div key={z._uid} className="grid grid-cols-[80px_1fr_60px] gap-2 items-center px-3 py-2 border-t border-white/5" data-testid={`payroll-zone-row-${i}`}>
                <input
                  type="text"
                  value={z.code || ""}
                  onChange={(e) => patchZone(i, { code: e.target.value.toUpperCase() })}
                  className="h-8 rounded-md bg-white/5 border border-white/10 px-2 text-sm font-bold text-center tracking-widest focus:outline-none focus:border-[#005CFF]"
                  data-testid={`payroll-zone-code-${i}`}
                  maxLength={4}
                />
                <div className="flex items-center gap-2 text-sm text-white/70">
                  <span className="tabular-nums w-[60px]">De {lowerLabel}</span>
                  <span className="text-white/40">à</span>
                  {isOpenEnd ? (
                    <span className="tabular-nums text-white/70">au-delà</span>
                  ) : (
                    <>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={z.upper_km ?? ""}
                        onChange={(e) => patchZone(i, { upper_km: e.target.value === "" ? null : Number(e.target.value) })}
                        className="h-8 w-20 rounded-md bg-white/5 border border-white/10 px-2 text-sm tabular-nums text-right focus:outline-none focus:border-[#005CFF]"
                        data-testid={`payroll-zone-upper-${i}`}
                      />
                      <span className="text-white/50">km</span>
                    </>
                  )}
                </div>
                <div className="text-right">
                  {zones.length > 1 && (
                    <button
                      onClick={() => removeZone(i)}
                      className="h-8 w-8 rounded-md bg-white/5 hover:bg-red-500/20 hover:text-red-400 text-white/50 inline-flex items-center justify-center"
                      data-testid={`payroll-zone-remove-${i}`}
                      title="Supprimer la zone"
                    >
                      <Trash size={13} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-2 mt-2">
          <button onClick={addZone} className="h-8 px-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-[11px] inline-flex items-center gap-1.5" data-testid="payroll-zone-add">
            <Plus size={11} weight="bold" /> Ajouter une zone
          </button>
          <button onClick={resetDefaults} className="h-8 px-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-[11px] inline-flex items-center gap-1.5 text-white/60" data-testid="payroll-zone-reset">
            <ArrowClockwise size={11} /> Grille URSSAF standard
          </button>
        </div>
      </div>

      {/* Data source */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Source de distance</div>
        <div className="space-y-1.5">
          {SOURCES.map((s) => {
            const Icon = s.icon;
            const active = dataSource === s.key;
            return (
              <button
                key={s.key}
                onClick={() => setDataSource(s.key)}
                className={`w-full text-left rounded-lg border px-3 py-2.5 transition-colors flex items-start gap-2.5 ${
                  active ? "bg-[#005CFF]/10 border-[#005CFF]/40" : "bg-white/[0.02] border-white/10 hover:bg-white/[0.04]"
                }`}
                data-testid={`payroll-source-${s.key}`}
              >
                <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${active ? "bg-[#005CFF] text-white" : "bg-white/5 text-white/50"}`}>
                  <Icon size={15} weight={active ? "fill" : "regular"} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-medium ${active ? "text-white" : "text-white/80"}`}>{s.label}</div>
                  <div className="text-[11px] text-white/50 leading-snug mt-0.5">{s.hint}</div>
                </div>
                {active && <div className="w-2 h-2 rounded-full bg-[#005CFF] mt-2 shrink-0" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Users included */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1.5">
            <Users size={12} /> Personnes soumises au calcul ({includedCount}/{team.length})
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setExcluded([])}
              className="text-[10px] text-white/50 hover:text-white px-2 h-6 rounded bg-white/5 hover:bg-white/10"
              data-testid="payroll-users-all"
            >
              Tout cocher
            </button>
            <button
              onClick={() => setExcluded(team.map((u) => u.id))}
              className="text-[10px] text-white/50 hover:text-white px-2 h-6 rounded bg-white/5 hover:bg-white/10"
              data-testid="payroll-users-none"
            >
              Tout décocher
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {team.length === 0 && (
            <div className="text-xs text-white/40 italic col-span-2">Aucun utilisateur.</div>
          )}
          {team.map((u) => {
            const included = !excluded.includes(u.id);
            return (
              <label
                key={u.id}
                className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 text-sm cursor-pointer transition-colors ${
                  included ? "bg-[#005CFF]/5 border-[#005CFF]/30 hover:border-[#005CFF]/50" : "bg-white/[0.02] border-white/10 hover:bg-white/[0.04]"
                }`}
                data-testid={`payroll-user-toggle-${u.id}`}
              >
                <input
                  type="checkbox"
                  checked={included}
                  onChange={() => toggleUser(u.id)}
                  className="accent-[#005CFF] w-4 h-4"
                />
                <span className="flex-1 min-w-0 truncate">{u.name}</span>
                <span className="text-[9px] uppercase tracking-widest text-white/40 shrink-0">{ROLE_LABEL[u.role] || u.role}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Footer save */}
      <div className="flex items-center justify-between pt-2 border-t border-white/10">
        <div className="text-[11px] text-white/40 leading-relaxed max-w-[70%]">
          Astuce : les tarifs (€) restent gérés par la comptable dans son logiciel de paie — cette grille sert uniquement à compter les zones + paniers.
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#005CFF] hover:bg-[#0047CC] h-9 px-4 text-sm font-semibold"
          data-testid="payroll-zone-save"
        >
          {saving ? "…" : "Enregistrer"}
        </Button>
      </div>
    </div>
  );
}
